/**
 * Auth UI Component
 * Uses Supabase Auth UI for pre-built authentication forms
 */

import { useRef, useEffect, useCallback } from 'react';
import { Auth } from '@supabase/auth-ui-react';
import { ThemeSupa } from '@supabase/auth-ui-shared';
import { supabase } from '@/config/supabase';
import { PasswordStrengthHint } from './PasswordStrengthHint';
import styles from './AuthUI.module.css';

export type AuthView = 'sign_in' | 'sign_up' | 'forgotten_password';

export interface AuthUIProps {
  view?: AuthView;
  /** Called when the user switches between sign-in / sign-up / forgot-password views */
  onViewChange?: (view: AuthView) => void;
}

/**
 * Detect the active Supabase Auth UI view by inspecting the rendered DOM.
 * The sign-up form uses `autocomplete="new-password"` on the password input,
 * whereas sign-in uses `autocomplete="current-password"`. If there is no
 * password input at all, the user is on the forgotten-password view.
 */
function detectAuthView(container: HTMLElement): AuthView {
  const passwordInput = container.querySelector<HTMLInputElement>('input#password');
  if (!passwordInput) return 'forgotten_password';
  if (passwordInput.autocomplete === 'new-password') return 'sign_up';
  return 'sign_in';
}

export function AuthUI({ view = 'sign_in', onViewChange }: AuthUIProps) {
  const cardRef = useRef<HTMLDivElement>(null);

  const emitViewChange = useCallback(() => {
    const container = cardRef.current;
    if (!container || !onViewChange) return;
    onViewChange(detectAuthView(container));
  }, [onViewChange]);

  // Observe DOM mutations to detect view switches within the Supabase Auth UI
  useEffect(() => {
    const container = cardRef.current;
    if (!container || !onViewChange) return;

    // Initial detection (after first render)
    const timer = requestAnimationFrame(() => emitViewChange());

    const observer = new MutationObserver(emitViewChange);
    observer.observe(container, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['autocomplete'],
    });

    return () => {
      cancelAnimationFrame(timer);
      observer.disconnect();
    };
  }, [emitViewChange, onViewChange]);

  return (
    <div className={styles.container}>
      <div className={styles.card} ref={cardRef}>
        <div className={styles.header}>
          <h1 className={styles.title}>Laser Tag Studio</h1>
          <p className={styles.subtitle}>
            {view === 'sign_up'
              ? 'Create an account to save your designs'
              : view === 'forgotten_password'
                ? 'Reset your password'
                : 'Sign in to access your designs'}
          </p>
        </div>

        <Auth
          supabaseClient={supabase}
          appearance={{
            theme: ThemeSupa,
            variables: {
              default: {
                colors: {
                  brand: '#8E5A28', // Darkened for 4.5:1 contrast with white text
                  brandAccent: '#6B4420',
                  brandButtonText: 'white',
                  defaultButtonBackground: '#2C2C2E',
                  defaultButtonBackgroundHover: '#3C3C3E',
                  defaultButtonBorder: '#48484A',
                  defaultButtonText: '#F5F5F5',
                  dividerBackground: '#48484A',
                  inputBackground: '#2C2C2E',
                  inputBorder: '#48484A',
                  inputBorderHover: '#6C6C6E',
                  inputBorderFocus: '#B87333',
                  inputText: '#F5F5F5',
                  inputLabelText: '#AEAEB2',
                  inputPlaceholder: '#8E8E93',
                  messageText: '#F5F5F5',
                  messageTextDanger: '#FF6B6B',
                  anchorTextColor: '#B87333',
                  anchorTextHoverColor: '#9A5F28',
                },
                space: {
                  spaceSmall: '8px',
                  spaceMedium: '12px',
                  spaceLarge: '16px',
                },
                fontSizes: {
                  baseBodySize: '14px',
                  baseInputSize: '14px',
                  baseLabelSize: '13px',
                  baseButtonSize: '14px',
                },
                borderWidths: {
                  buttonBorderWidth: '1px',
                  inputBorderWidth: '1px',
                },
                radii: {
                  borderRadiusButton: '8px',
                  buttonBorderRadius: '8px',
                  inputBorderRadius: '8px',
                },
              },
            },
            style: {
              button: {
                minHeight: '44px', // WCAG touch target size
              },
            },
            className: {
              container: styles.authContainer,
              button: styles.authButton,
              input: styles.authInput,
              label: styles.authLabel,
            },
          }}
          theme="dark"
          view={view}
          providers={['google']}
          redirectTo={window.location.origin}
          showLinks={true}
          onlyThirdPartyProviders={false}
          localization={{
            variables: {
              sign_in: {
                email_label: 'Email',
                password_label: 'Password',
                button_label: 'Sign In',
                link_text: 'Already have an account? Sign In',
              },
              sign_up: {
                email_label: 'Email',
                password_label: 'Password',
                button_label: 'Sign Up',
                link_text: "Don't have an account? Sign Up",
              },
              forgotten_password: {
                email_label: 'Email',
                button_label: 'Send Reset Link',
                link_text: 'Forgot your password?',
              },
            },
          }}
        />

        <PasswordStrengthHint containerRef={cardRef} />

        <p className={styles.footer}>
          By continuing, you agree to our{' '}
          <a href="#/terms" target="_blank" rel="noopener noreferrer" className={styles.footerLink}>
            Terms of Service
          </a>{' '}
          and{' '}
          <a
            href="#/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.footerLink}
          >
            Privacy Policy
          </a>
        </p>
      </div>
    </div>
  );
}
